import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import OperadoraSelector from './components/OperadoraSelector';
import {
  getToken,
  clearToken,
  getOperadoras,
  getOperadora,
  OperadoraMeta,
  OperadoraPayload,
  ApiError,
} from './api';

enum AppState {
  LOGIN,
  SELECAO_OPERADORA,
  DASHBOARD,
}

const initialParam = (): string => new URLSearchParams(window.location.search).get('operadora') || '';

function App() {
  const [operadoraSelecionada, setOperadoraSelecionada] = useState<string>(initialParam());
  const [appState, setAppState] = useState<AppState>(() => {
    if (!getToken()) return AppState.LOGIN;
    return initialParam() ? AppState.DASHBOARD : AppState.SELECAO_OPERADORA;
  });
  const [operadoras, setOperadoras] = useState<OperadoraMeta[]>([]);
  const [payload, setPayload] = useState<OperadoraPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const setUrlParam = (value: string | null) => {
    const url = new URL(window.location.href);
    if (value) url.searchParams.set('operadora', value);
    else url.searchParams.delete('operadora');
    window.history.pushState({}, '', url);
  };

  const handleLogout = useCallback(() => {
    clearToken();
    setOperadoraSelecionada('');
    setOperadoras([]);
    setPayload(null);
    setError('');
    setAppState(AppState.LOGIN);
    setUrlParam(null);
  }, []);

  // Carregar lista de operadoras ao entrar na seleção
  useEffect(() => {
    if (appState !== AppState.SELECAO_OPERADORA || operadoras.length > 0) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    getOperadoras()
      .then((list) => {
        if (!cancelled) setOperadoras(list);
      })
      .catch((err: ApiError) => {
        if (cancelled) return;
        if (err.status === 401) handleLogout();
        else setError(err.message || 'Erro ao carregar operadoras.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appState, operadoras.length, handleLogout]);

  // Carregar dados da operadora ao entrar no dashboard
  useEffect(() => {
    if (appState !== AppState.DASHBOARD || !operadoraSelecionada) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setPayload(null);
    getOperadora(operadoraSelecionada)
      .then((p) => {
        if (!cancelled) setPayload(p);
      })
      .catch((err: ApiError) => {
        if (cancelled) return;
        if (err.status === 401) {
          handleLogout();
        } else {
          // Operadora inválida/inexistente: volta à seleção com aviso
          setError(err.message || 'Não foi possível carregar a operadora.');
          setUrlParam(null);
          setAppState(AppState.SELECAO_OPERADORA);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appState, operadoraSelecionada, handleLogout]);

  const handleLogin = () => {
    setAppState(operadoraSelecionada ? AppState.DASHBOARD : AppState.SELECAO_OPERADORA);
  };

  const handleOperadoraSelect = (operadoraId: string, shouldMask: boolean = false) => {
    setOperadoraSelecionada(operadoraId);
    setAppState(AppState.DASHBOARD);
    const masked =
      payload?.maskedId || operadoras.find((o) => o.id === operadoraId)?.maskedId || operadoraId;
    setUrlParam(shouldMask ? masked : operadoraId);
  };

  const handleChangeOperadora = () => {
    setPayload(null);
    setError('');
    setAppState(AppState.SELECAO_OPERADORA);
    setUrlParam(null);
  };

  const renderAppState = () => {
    switch (appState) {
      case AppState.LOGIN:
        return <Login onLogin={handleLogin} />;

      case AppState.SELECAO_OPERADORA:
        return (
          <>
            {error && (
              <div className="bg-red-50 border-b border-red-200 text-red-700 text-sm text-center py-2 px-4">
                {error}
              </div>
            )}
            {loading && operadoras.length === 0 ? (
              <div className="min-h-screen flex items-center justify-center text-gray-500">
                Carregando operadoras...
              </div>
            ) : (
              <OperadoraSelector
                operadoras={operadoras}
                onSelect={(id) => handleOperadoraSelect(id, false)}
                onLogout={handleLogout}
              />
            )}
          </>
        );

      case AppState.DASHBOARD:
        if (loading || !payload) {
          return (
            <div className="min-h-screen flex items-center justify-center text-gray-500">
              Carregando dados...
            </div>
          );
        }
        return (
          <Dashboard
            key={payload.id}
            operadoraId={payload.id}
            data={payload.data}
            dataPeriodoNovo={payload.dataPeriodoNovo}
            headerImageUrl={payload.headerImageUrl}
            onLogout={handleLogout}
            onChangeOperadora={handleChangeOperadora}
            onUrlMaskChange={handleOperadoraSelect}
          />
        );

      default:
        return <Login onLogin={handleLogin} />;
    }
  };

  return <div className="App">{renderAppState()}</div>;
}

export default App;
